import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Quote, Instagram, Star, MapPin } from "lucide-react";
import { fetchTestimoni } from "@/lib/api";

const initials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};

const igHandle = (raw) => raw ? raw.trim().replace(/^@/, "") : "";
const igUrl = (raw) => {
  const handle = igHandle(raw);
  return handle ? `https://instagram.com/${handle}` : "";
};

const Stars = ({ count = 5 }) => (
  <div className="flex items-center gap-0.5">
    {[...Array(count)].map((_, i) => (
      <Star key={i} className="h-3.5 w-3.5 fill-current" style={{ color: "#C8A355" }} />
    ))}
  </div>
);

const FeaturedCard = ({ t }) => (
  <motion.article
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6 }}
    className="relative grid md:grid-cols-2 bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-md"
  >
    <div className="relative h-64 md:h-full min-h-[280px] bg-gray-100">
      {t.foto ? (
        <img src={t.foto} alt={t.nama} className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div
          className="absolute inset-0 flex items-center justify-center text-6xl font-serif font-semibold text-white"
          style={{ backgroundColor: "#8B1A1A" }}
        >
          {initials(t.nama)}
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 md:from-black/10 md:to-transparent" />
      {t.periode && (
        <div className="absolute top-5 left-5 inline-flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur px-3 py-1 text-[10px] font-semibold tracking-wide uppercase text-gray-800">
          {t.periode}
        </div>
      )}
    </div>

    <div className="flex flex-col justify-center p-8 sm:p-10">
      <Quote className="h-9 w-9 mb-4 opacity-15" style={{ color: "#8B1A1A" }} strokeWidth={2.5} />
      <p className="text-gray-800 text-base sm:text-lg leading-relaxed font-serif italic">
        "{t.isi}"
      </p>
      <div className="mt-7 flex items-center gap-3">
        <div className="min-w-0">
          <div className="font-semibold text-gray-900">{t.nama}</div>
          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
            {t.kota && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {t.kota}
              </span>
            )}
            {t.instagram && (
              <a
                href={igUrl(t.instagram)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 hover:text-[#8B1A1A] transition-colors"
              >
                <Instagram className="h-3 w-3" /> @{igHandle(t.instagram)}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  </motion.article>
);

const TestimonialCard = ({ t, index }) => (
  <motion.article
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55, delay: (index % 3) * 0.1 }}
    className="flex flex-col bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow duration-500 overflow-hidden"
  >
    <div className="flex items-center gap-3 p-5 pb-0">
      {t.foto ? (
        <img
          src={t.foto}
          alt={t.nama}
          className="h-14 w-14 rounded-full object-cover flex-shrink-0 ring-2 ring-[#F5E9D3]"
        />
      ) : (
        <div
          className="h-14 w-14 rounded-full flex items-center justify-center flex-shrink-0 text-base font-semibold text-white ring-2 ring-[#F5E9D3]"
          style={{ backgroundColor: "#8B1A1A" }}
        >
          {initials(t.nama)}
        </div>
      )}
      <div className="min-w-0">
        <div className="font-semibold text-sm text-gray-900 truncate">{t.nama}</div>
        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5 flex-wrap">
          {t.kota && <span>{t.kota}</span>}
          {t.instagram && (
            <a
              href={igUrl(t.instagram)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 hover:text-[#8B1A1A] transition-colors"
            >
              <Instagram className="h-3 w-3" /> @{igHandle(t.instagram)}
            </a>
          )}
        </div>
      </div>
    </div>

    <div className="p-5 pt-4 flex-1 flex flex-col">
      <Stars />
      <p className="mt-3 text-gray-700 text-sm leading-relaxed flex-1">
        "{t.isi}"
      </p>
      {t.periode && (
        <div className="mt-4 pt-3 border-t border-gray-100 text-[11px] uppercase tracking-wide text-gray-400">
          {t.periode}
        </div>
      )}
    </div>
  </motion.article>
);

export const Testimonials = () => {
  const [testimoni, setTestimoni] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchTestimoni()
      .then((data) => {
        if (cancelled) return;
        setTestimoni(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loading && testimoni.length === 0) return null;

  const [featured, ...rest] = testimoni;

  return (
    <section
      id="testimoni"
      data-testid="testimonials-section"
      className="relative bg-[#FAF8F4] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mx-auto text-center"
        >
          <div className="divider-ornament mx-auto mb-5">
            <span className="text-[11px] tracking-[0.35em] uppercase">
              Cerita Jamaah
            </span>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-gray-900 tracking-tight leading-tight">
            Kata mereka yang{" "}
            <span className="italic text-[#8B1A1A]">sudah berangkat</span>
          </h2>
          <p className="mt-5 text-gray-600 text-base sm:text-lg leading-relaxed">
            Pengalaman nyata jamaah yang telah mempercayakan perjalanan
            ibadahnya bersama kami.
          </p>
        </motion.div>

        {loading && (
          <p className="mt-16 text-center text-gray-400 text-sm">Memuat testimoni...</p>
        )}

        {!loading && featured && (
          <div className="mt-16">
            <FeaturedCard t={featured} />
          </div>
        )}

        {!loading && rest.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7 items-stretch">
            {rest.map((t, i) => (
              <TestimonialCard key={t.id} t={t} index={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;
